import { catchError, concatMap, forkJoin, from, Observable, of, timeout } from 'rxjs';
import { switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OnlineService } from '../online.service';
import extend from 'lodash-es/extend';
import { ConfigurationManager } from './configuration-manager';
import { LogService } from '../log/log.service';
import { TIMEOUT_CACHE } from '../cache/cache-settings';
import { EnvironmentConfiguration } from '@nts/std/environments';
import { LocalStorageService } from '../storage/local-storage.service';
import { LocalstorageHelper } from '../storage/localstorage-helper';
import { SerializabledDataType } from '@nts/std/types';

export enum ConfigurationLoaderFileType {
  StdConfiguration = 'stdConfiguration',
  Configuration = 'configuration'
}

export const CONFIGURATION_LOADER_STORAGE_KEY = 'jsonConfiguration';

type ConfigMap = {
  [ConfigurationLoaderFileType.StdConfiguration]: any;
  [ConfigurationLoaderFileType.Configuration]: any;
};

@Injectable({
  providedIn: 'root',
})
export class ConfigurationLoader {

  constructor(
    private http: HttpClient,
    private onlineService: OnlineService,
    private localStorageService: LocalStorageService,
    private env: EnvironmentConfiguration,
  ) {
  }

  getConfiguration(): Observable<any> {
    return from(this.localStorageService.init()).pipe(
      concatMap(() => from(LocalstorageHelper.getStorageItem({
        key: CONFIGURATION_LOADER_STORAGE_KEY,
        tenantBarrier: false,
        enterpriseBarrier: false,
        userBarrier: false,
        companyBarrier: false
      }))),
      switchMap(stored => this.loadConfigurations(stored)),
      concatMap(configs => from(this.persistAndBuildConfiguration(configs)))
    );
  }

  private loadConfigurations(
    storedJsonConfiguration: SerializabledDataType | undefined
  ): Observable<ConfigMap> {
    if (this.onlineService.isOnline === false) {
      return of({
        [ConfigurationLoaderFileType.StdConfiguration]:
          storedJsonConfiguration?.[ConfigurationLoaderFileType.StdConfiguration] ?? {},
        [ConfigurationLoaderFileType.Configuration]:
          storedJsonConfiguration?.[ConfigurationLoaderFileType.Configuration] ?? {},
      });
    }

    return forkJoin({
      [ConfigurationLoaderFileType.StdConfiguration]: this.http
        .get('assets/std/configuration.json')
        .pipe(
          catchError((e) => {
            LogService.warn('missing std configuration.json!', e);
            return of({});
          })
        ),

      [ConfigurationLoaderFileType.Configuration]: this.http
        .get('assets/configuration.json')
        .pipe(
          timeout({
            first: TIMEOUT_CACHE,
            with: () =>
              of(
                storedJsonConfiguration?.[ConfigurationLoaderFileType.Configuration] ?? {}
              ),
          }),
          catchError((e) => {
            LogService.warn('missing ms configuration.json!', e);
            return of(
              storedJsonConfiguration?.[ConfigurationLoaderFileType.Configuration] ?? {}
            );
          })
        ),
    });
  }

  /**
   * Persiste in cache la configurazione e costruisce la configurazione finale estendendo quella standard con quella custom (se presente)
   * @param configurations
   * @returns
   */
  private async persistAndBuildConfiguration(configurations: ConfigMap): Promise<any> {
    const hasSomething =
      !!configurations[ConfigurationLoaderFileType.StdConfiguration] ||
      !!configurations[ConfigurationLoaderFileType.Configuration];

    if (hasSomething) {
      await LocalstorageHelper.setStorageItem(
        CONFIGURATION_LOADER_STORAGE_KEY,
        configurations,
        undefined,
        false,
        false
      );
    }

    const extendedRes = extend(
      {},
      configurations[ConfigurationLoaderFileType.StdConfiguration],
      configurations[ConfigurationLoaderFileType.Configuration]
    );

    ConfigurationManager.Current.config = extendedRes;

    this.env.baseAppUrl = (
      ConfigurationManager?.Current?.config?.baseAppUrl === '' ||
      ConfigurationManager?.Current?.config?.baseAppUrl == null
    ) ?
      this.env.baseAppUrl :
      ConfigurationManager?.Current?.config?.baseAppUrl;

    // Remove slash and backslash at the end of the string
    this.env.baseAppUrl = this.env.baseAppUrl.endsWith('/') ? this.env.baseAppUrl.slice(0, -1) : this.env.baseAppUrl;
    this.env.baseAppUrl = this.env.baseAppUrl.endsWith('\\') ? this.env.baseAppUrl.slice(0, -1) : this.env.baseAppUrl;

    this.env.authenticationAppUrl = (
      ConfigurationManager?.Current?.config?.authenticationAppUrl === '' ||
      ConfigurationManager?.Current?.config?.authenticationAppUrl == null
    ) ?
      this.env.authenticationAppUrl :
      ConfigurationManager?.Current?.config?.authenticationAppUrl;

    // Remove slash and backslash at the end of the string
    this.env.authenticationAppUrl = this.env.authenticationAppUrl.endsWith('/') ? this.env.authenticationAppUrl.slice(0, -1) : this.env.authenticationAppUrl;
    this.env.authenticationAppUrl = this.env.authenticationAppUrl.endsWith('\\') ? this.env.authenticationAppUrl.slice(0, -1) : this.env.authenticationAppUrl;

    return extendedRes;
  }
}
